import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth, kmsService } from '@aaas/shared';
import type { WidgetFormData } from '../types/widget.types';

interface UseWidgetListQueryOptions {
  skip?: number;
  limit?: number;
  enabled?: boolean;
}

/**
 * React Query hook for fetching widget list
 *
 * Features:
 * - Smart caching with stale-while-revalidate
 * - Auto refetch on window focus
 * - Request deduplication
 */
export function useWidgetListQuery({
  skip = 0,
  limit = 10,
  enabled = true,
}: UseWidgetListQueryOptions = {}) {
  const { idToken } = useAuth();

  return useQuery({
    queryKey: ['kms-widgets', skip, limit],
    queryFn: async () => {
      if (!idToken) {
        throw new Error('Authentication token required');
      }

      const response = await kmsService.listWidgets(idToken, { skip, limit });
      return response.data;
    },
    enabled: enabled && !!idToken,
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true,
  });
}

interface UseWidgetQueryOptions {
  widgetId: string | undefined;
  enabled?: boolean;
}

/**
 * React Query hook for fetching single widget details
 */
export function useWidgetQuery({ widgetId, enabled = true }: UseWidgetQueryOptions) {
  const { idToken } = useAuth();

  return useQuery({
    queryKey: ['kms-widget', widgetId],
    queryFn: async () => {
      if (!idToken || !widgetId) {
        throw new Error('Widget ID and authentication token required');
      }

      const response = await kmsService.getWidget(idToken, widgetId);
      return response.data;
    },
    enabled: enabled && !!idToken && !!widgetId,
    staleTime: 30000, // Fresh for 30 seconds
    refetchOnWindowFocus: true,
  });
}

/**
 * React Query mutation hook for creating widgets
 */
export function useCreateWidgetMutation() {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: WidgetFormData) => {
      if (!idToken) {
        throw new Error('Authentication token required');
      }

      const response = await kmsService.createWidget(idToken, data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate widget list queries to refetch
      queryClient.invalidateQueries({
        queryKey: ['kms-widgets'],
      });
    },
  });
}

/**
 * React Query mutation hook for updating widgets
 */
export function useUpdateWidgetMutation(widgetId: string) {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<WidgetFormData>) => {
      if (!idToken) {
        throw new Error('Authentication token required');
      }

      const response = await kmsService.updateWidget(idToken, widgetId, data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate both list and specific widget queries
      queryClient.invalidateQueries({
        queryKey: ['kms-widgets'],
      });
      queryClient.invalidateQueries({
        queryKey: ['kms-widget', widgetId],
      });
    },
  });
}

/**
 * React Query mutation hook for deleting widgets
 */
export function useDeleteWidgetMutation() {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (widgetId: string) => {
      if (!idToken) {
        throw new Error('Authentication token required');
      }

      await kmsService.deleteWidget(idToken, widgetId);
      return { widgetId };
    },
    onSuccess: () => {
      // Invalidate widget list to refetch
      queryClient.invalidateQueries({
        queryKey: ['kms-widgets'],
      });
    },
  });
}

/**
 * React Query hook for fetching widget embed code
 */
export function useWidgetEmbedCodeQuery({
  widgetId,
  spaceId,
  enabled = false, // Disabled by default - only fetch when explicitly requested
}: {
  widgetId: string | undefined;
  spaceId: string | undefined;
  enabled?: boolean;
}) {
  const { idToken } = useAuth();

  return useQuery({
    queryKey: ['kms-widget-embed', widgetId, spaceId],
    queryFn: async () => {
      if (!idToken || !widgetId || !spaceId) {
        throw new Error('Widget ID, Space ID, and authentication token required');
      }

      const response = await kmsService.getWidgetEmbedCode(idToken, widgetId, spaceId);
      return response.data;
    },
    enabled: enabled && !!idToken && !!widgetId && !!spaceId,
    staleTime: 60000, // Embed code is stable, fresh for 1 minute
    refetchOnWindowFocus: false, // Don't refetch embed code on focus
  });
}
